import { expose } from 'comlink';
import { collection, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, query, Query, startAfter } from 'firebase/firestore';
import { Warehouse } from '../../warehouse/warehouse.dto';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { IdbUpright } from '../../asset/asset.dto';
import { merge } from 'lodash-es';
import { QueryConstraint } from '@angular/fire/firestore';
import { ASSETS_COLLECTION, UPRIGHTS_STORE, WAREHOUSES_STORE, WW_WAREHOUSES_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseUprightsStepsWorker extends AbstractWarehouseDataSyncStepWorker<IdbUpright> {
  constructor() {
    super(UPRIGHTS_STORE, 'UPRIGHTS');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseUprightsStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<IdbUpright>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: IdbUpright[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    const q: Query = query(collection(getFirestore(), `${WW_WAREHOUSES_COLLECTION}/${ctx.division}/${ASSETS_COLLECTION}`), orderBy('assetId'), ...queryConstraints);
    const queryResult = await getDocs(q);

    if (queryResult.docs.length && !this.shouldStopTask) {
      const uprights = [];
      for (const i of queryResult.docs) {
        let matching: IdbUpright & { syncId?: string } = allFromIndex.find((idbDoc) => idbDoc._id === i.id);
        if (matching) {
          merge(matching, { ...i.data(), syncId: ctx.syncId });
        } else {
          matching = { ...i.data(), division: { _id: ctx.division }, _id: i.id, syncId: ctx.syncId };
        }

        uprights.push(matching);
      }

      if (!this.shouldStopTask) {
        logger.info('Downloaded uprights from Firestore', { count: uprights.length, category: 'sync' });
        await this.idbService.updateAll(this.store, uprights).catch(console.error);
        return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
      }
    }

    return [null, 0];
  }

  protected async updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    const warehouseData: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouse);
    if (warehouseData?.statistics?.assets) {
      const newProgress = Math.ceil((downloaded / warehouseData.statistics?.assets) * 100);
      logger.debug('Total items downloaded', { progress: newProgress, category: 'sync' });
      onProgress(newProgress, { processed: downloaded, expected: warehouseData.statistics.assets });
    }
  }
}

expose(WarehouseUprightsStepsWorker);
